import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import TravelsUSMap from "@/components/TravelsUSMap";

const STATUS_COLORS = {
  past: "bg-[var(--line)] text-[var(--ink-soft)]",
  current: "bg-[var(--terracotta)] text-[var(--ink)]",
  upcoming: "bg-[var(--forest)] text-white",
};

export default function Travels() {
  const [destinations, setDestinations] = useState([]);
  const [activeDest, setActiveDest] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/destinations");
    setDestinations(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="travels-page" className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline mb-4">The road of hope</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-4">On the road</h1>
      <p className="text-[var(--ink-soft)] text-lg max-w-2xl leading-relaxed mb-16">
        Every dot is a city we've already met people in, are standing in today, or are headed to next.
        Click a stop to leave a note — or to tell us you'll be there.
      </p>

      <TravelsUSMap destinations={destinations} />

      {destinations.length === 0 ? (
        <div className="text-center py-24 text-[var(--ink-soft)]">No destinations posted yet — check back soon.</div>
      ) : (
        <div className="relative tracing-line pl-12">
          {destinations.map((d, i) => (
            <div key={d.id} data-testid={`dest-${d.id}`} className="relative mb-14 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`absolute -left-12 top-2 w-6 h-6 rounded-full border-2 border-[var(--bg)] ${d.status === "current" ? "bg-[var(--terracotta)] ring-4 ring-[var(--terracotta)]/20" : d.status === "upcoming" ? "bg-[var(--forest)]" : "bg-[var(--line)]"}`}></span>

              <div className="flex flex-wrap items-baseline gap-3 mb-2">
                <span className={`text-xs px-3 py-1 rounded-full ${STATUS_COLORS[d.status]}`}>{d.status}</span>
                <span className="font-mono-acc text-xs text-[var(--ink-soft)]">{d.start_date} {d.end_date && `→ ${d.end_date}`}</span>
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl mb-2 flex items-center gap-3">
                {d.location}
                <span className="text-[var(--ink-soft)] text-base font-sans">· {d.country}</span>
              </h2>
              <div className="text-lg text-[var(--ink-soft)] mb-3 italic">{d.title}</div>
              {d.image_url && <img src={d.image_url} alt={d.location} className="w-full md:w-2/3 h-72 object-cover rounded-sm mb-4" />}
              <p className="text-[var(--ink-soft)] leading-relaxed max-w-3xl">{d.description}</p>

              <button data-testid={`guestbook-toggle-${d.id}`}
                onClick={() => setActiveDest(activeDest === d.id ? null : d.id)}
                className="inline-flex items-center gap-2 mt-5 text-sm text-[var(--forest)] hover:underline">
                <MessageSquare className="w-4 h-4" /> Guestbook {activeDest === d.id ? "(hide)" : "(view & post)"}
              </button>

              {activeDest === d.id && <Guestbook destinationId={d.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Guestbook({ destinationId }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/guestbook/${destinationId}`);
    setEntries(data);
  }, [destinationId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setPosting(true);
    try {
      await api.post("/guestbook", { destination_id: destinationId, message });
      setMessage("");
      load();
      toast.success("Posted to the guestbook!");
    } catch (err) {
      toast.error("Failed to post. Please try again.");
    }
    setPosting(false);
  };

  return (
    <div className="mt-5 p-6 md:p-8 rounded-sm" style={{ background: "var(--bg-alt)" }}>
      {entries.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] italic mb-4">No notes yet — be the first to leave one.</p>
      ) : (
        <ul className="space-y-4 mb-6">
          {entries.map((en) => (
            <li key={en.id} className="border-l-2 border-[var(--terracotta)]/40 pl-4">
              <div className="text-sm font-medium">{en.user_name}</div>
              <div className="text-sm text-[var(--ink-soft)] mt-1">{en.message}</div>
              <div className="font-mono-acc text-[10px] text-[var(--ink-soft)] mt-1">{en.created_at?.slice(0, 10)}</div>
            </li>
          ))}
        </ul>
      )}

      {user && user !== false ? (
        <form onSubmit={submit} className="flex gap-2" data-testid={`guestbook-form-${destinationId}`}>
          <input
            data-testid={`guestbook-input-${destinationId}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Leave a note for the journey…"
            className="flex-1 bg-[var(--bg)] border border-[var(--line)] rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--terracotta)]"
          />
          <button data-testid={`guestbook-submit-${destinationId}`} disabled={posting}
            className="bg-[var(--terracotta)] text-[var(--ink)] px-5 py-2.5 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50 inline-flex items-center gap-2">
            <Send className="w-4 h-4" /> Post
          </button>
        </form>
      ) : (
        <p className="text-sm text-[var(--ink-soft)]">
          <Link to="/login" className="text-[var(--forest)] underline">Sign in</Link> to leave a note.
        </p>
      )}
    </div>
  );
}
